import { useState, useEffect } from "react";
import { Sparkles, Heart, Salad, Utensils, Flame, Shuffle, Plus, X, RefreshCw, Coffee, Soup, UtensilsCrossed, Cake } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const CATEGORIES = [
  { id: "sunn",       label: "Sunn mat",         Icon: Salad,    color: "bg-emerald-50 text-emerald-800 border-emerald-200 hover:bg-emerald-100",   active: "bg-emerald-600 border-emerald-600 text-white",  desc: "Mye grønnsaker, lite fett, fiberrikt" },
  { id: "hverdag",    label: "Hverdagsmat",      Icon: Utensils, color: "bg-amber-50 text-amber-800 border-amber-200 hover:bg-amber-100",           active: "bg-amber-600 border-amber-600 text-white",      desc: "Raskt og enkelt, familievennlig" },
  { id: "tradisjon",  label: "Tradisjonsmat",    Icon: Flame,    color: "bg-red-50 text-red-800 border-red-200 hover:bg-red-100",                   active: "bg-red-600 border-red-600 text-white",          desc: "Klassisk norsk: komle, lapskaus, fiskesuppe..." },
  { id: "mix",        label: "Blanding",         Icon: Shuffle,  color: "bg-sky-50 text-sky-800 border-sky-200 hover:bg-sky-100",                   active: "bg-sky-600 border-sky-600 text-white",          desc: "Varier mellom alle kategoriene" },
];

const MEAL_TYPES = [
  { id: "breakfast", label: "Frokost", Icon: Coffee },
  { id: "lunch",     label: "Lunsj",   Icon: Soup },
  { id: "dinner",    label: "Middag",  Icon: UtensilsCrossed },
  { id: "dessert",   label: "Dessert", Icon: Cake },
];

export default function AIMealPlanDialog({ open, onOpenChange, weekStart, onApply, initialCategory = "mix" }) {
  const { user, activeBoat } = useAuth();
  const [category, setCategory] = useState(initialCategory);
  const [days, setDays] = useState(activeBoat?.household_days || 7);
  const [servings, setServings] = useState(activeBoat?.num_onboard || 6);
  const [wishes, setWishes] = useState([]);
  const [newWish, setNewWish] = useState("");
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [selectedMeals, setSelectedMeals] = useState(["breakfast", "lunch", "dinner"]);

  const toggleMeal = (id) => {
    setSelectedMeals((prev) => prev.includes(id) ? prev.filter((m) => m !== id) : [...prev, id]);
  };

  useEffect(() => {
    if (!open) return;
    setDays(activeBoat?.household_days || 7);
    setServings(activeBoat?.num_onboard || 6);
    setCategory(initialCategory || "mix");
    loadWishes();
  }, [open, activeBoat, initialCategory]);

  const loadWishes = async () => {
    setLoading(true);
    try {
      const res = await api.getMealWishes();
      setWishes(res.data?.wishes || []);
    } catch { /* silent */ }
    finally { setLoading(false); }
  };

  const addWish = async () => {
    const txt = newWish.trim();
    if (!txt) return;
    try {
      const res = await api.addMealWish(txt);
      setWishes((w) => [res.data, ...w]);
      setNewWish("");
    } catch (err) { toast.error(err.response?.data?.detail || "Feil"); }
  };

  const removeWish = async (id) => {
    setWishes((w) => w.filter((x) => x.id !== id));
    try { await api.deleteMealWish(id); } catch { /* silent */ }
  };

  const handleGenerate = async () => {
    if (selectedMeals.length === 0) { toast.error("Velg minst ett måltid"); return; }
    setGenerating(true);
    toast.info(`Genererer ${days}-dagers ${CATEGORIES.find((c) => c.id === category).label.toLowerCase()}-plan...`);
    try {
      const res = await api.aiGenerateWeekPlan(weekStart, { category, days, servings, meals: selectedMeals });
      const suggestion = res.data?.suggestion || {};
      const keys = Object.keys(suggestion);
      if (keys.length === 0) { toast.error("AI ga ingen forslag – prøv igjen"); return; }
      const catLabel = CATEGORIES.find((c) => c.id === category)?.label || category;
      toast.success(`${catLabel}-plan generert: ${keys.length} dager${res.data.wish_count ? ` · ${res.data.wish_count} ønsker` : ""}`);
      onApply && onApply(suggestion);
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Kunne ikke generere meny");
    } finally { setGenerating(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="ai-meal-plan-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            <Sparkles className="h-5 w-5 text-orange-600" /> AI matplan
          </DialogTitle>
          <DialogDescription>
            Velg kategori, antall dager og personer – AI-en foreslår en hel ukemeny basert på ønsker fra {activeBoat?.kind === "household" ? "husstanden" : "mannskapet"}.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Category chips */}
          <div>
            <label className="text-[11px] uppercase tracking-wider text-stone-500 font-semibold mb-2 block">Kategori</label>
            <div className="grid grid-cols-2 gap-2">
              {CATEGORIES.map((c) => {
                const Icon = c.Icon;
                const isActive = category === c.id;
                return (
                  <button
                    key={c.id}
                    onClick={() => setCategory(c.id)}
                    className={`flex items-center gap-2 rounded-lg border-2 px-3 py-2 text-xs font-semibold transition-all text-left ${isActive ? c.active : c.color}`}
                    data-testid={`cat-${c.id}`}
                  >
                    <Icon className="h-4 w-4 flex-shrink-0" />
                    <div className="min-w-0">
                      <div>{c.label}</div>
                      <div className={`text-[10px] font-normal opacity-80 truncate`}>{c.desc}</div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Meal type checkboxes */}
          <div>
            <label className="text-[11px] uppercase tracking-wider text-stone-500 font-semibold mb-2 block">Hvilke måltider?</label>
            <div className="grid grid-cols-2 gap-2" data-testid="meal-type-grid">
              {MEAL_TYPES.map((m) => {
                const Icon = m.Icon;
                const active = selectedMeals.includes(m.id);
                return (
                  <button
                    key={m.id}
                    type="button"
                    onClick={() => toggleMeal(m.id)}
                    className={`flex items-center gap-2 rounded-lg border-2 px-3 py-2 text-xs font-semibold transition-all ${active ? "border-orange-600 bg-orange-50 text-orange-800" : "border-stone-200 text-stone-400 hover:border-stone-300"}`}
                    data-testid={`meal-toggle-${m.id}`}
                  >
                    <Icon className="h-4 w-4 flex-shrink-0" />
                    <span className="flex-1 text-left">{m.label}</span>
                    {active && <span className="text-[10px] font-bold">✓</span>}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Days + servings */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[11px] uppercase tracking-wider text-stone-500 font-semibold">Antall dager</label>
              <input
                type="number" min="1" max="21"
                value={days}
                onChange={(e) => setDays(Number(e.target.value) || 7)}
                className="mt-1 w-full rounded-md border border-stone-200 bg-white px-3 py-2 text-sm outline-none focus:border-orange-500"
                data-testid="ai-days-input"
              />
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-wider text-stone-500 font-semibold">Personer</label>
              <input
                type="number" min="1" max="30"
                value={servings}
                onChange={(e) => setServings(Number(e.target.value) || 6)}
                className="mt-1 w-full rounded-md border border-stone-200 bg-white px-3 py-2 text-sm outline-none focus:border-orange-500"
                data-testid="ai-servings-input"
              />
            </div>
          </div>

          {/* Wishes */}
          <div>
            <label className="text-[11px] uppercase tracking-wider text-stone-500 font-semibold mb-2 block flex items-center gap-1.5">
              <Heart className="h-3 w-3 text-red-400" /> Ønsker fra {activeBoat?.kind === "household" ? "husstanden" : "mannskapet"} ({wishes.length})
            </label>
            <div className="flex gap-2 mb-2">
              <input
                type="text"
                value={newWish}
                onChange={(e) => setNewWish(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") addWish(); }}
                placeholder="f.eks. Taco fredag, fiskegrateng, komle til helgen..."
                className="flex-1 rounded-md border border-stone-200 bg-white px-3 py-2 text-sm outline-none focus:border-orange-500"
                data-testid="ai-wish-input"
              />
              <button
                onClick={addWish}
                disabled={!newWish.trim()}
                className="rounded-md bg-navy-800 text-white px-3 py-2 text-sm font-semibold hover:bg-navy-900 disabled:opacity-50"
                data-testid="add-wish-btn"
              >
                <Plus className="h-4 w-4" />
              </button>
            </div>
            <div className="space-y-1.5 max-h-44 overflow-y-auto" data-testid="wish-list">
              {loading ? <div className="text-xs text-stone-400 text-center py-2">Laster...</div> : wishes.length === 0 ? (
                <p className="text-xs text-stone-400 italic">Ingen ønsker ennå. Legg til for å påvirke menyen.</p>
              ) : wishes.map((w) => (
                <div key={w.id} className="flex items-center gap-2 bg-stone-50 border border-stone-100 rounded-md px-3 py-1.5 text-xs" data-testid={`wish-${w.id}`}>
                  <Heart className="h-3 w-3 text-red-400 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <span className="text-stone-800">{w.text}</span>
                    <span className="text-stone-400 ml-1.5">– {w.author_name}</span>
                  </div>
                  {w.user_id === user?.user_id && (
                    <button onClick={() => removeWish(w.id)} className="text-stone-400 hover:text-red-500" title="Fjern ønske">
                      <X className="h-3 w-3" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>

          <button
            onClick={handleGenerate}
            disabled={generating}
            className={`w-full rounded-md px-4 py-3 text-sm font-bold text-white transition-colors disabled:opacity-50 ${generating ? "bg-stone-500" : "bg-gradient-to-r from-orange-600 to-red-600 hover:from-orange-700 hover:to-red-700"}`}
            data-testid="generate-meal-plan-btn"
          >
            {generating ? (
              <span className="inline-flex items-center gap-2"><RefreshCw className="h-4 w-4 animate-spin" /> Genererer...</span>
            ) : (
              <span className="inline-flex items-center gap-2"><Sparkles className="h-4 w-4" /> Generer {days}-dagers plan</span>
            )}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
